import React, { useEffect, useState } from "react";
import ItemConfigDetails from "../components/Item_Configuration_Page/ItemConfigDetails";
import ItemConfigForm from "../components/Item_Configuration_Page/ItemConfigForm";
import DashboardNavBar from "../components/NavBar/DashboardNavBar";
import Footer from "../components/Shared/Footer";
import { useItemConfigContext } from "../hooks/useContext/useItemConfigContext";
import { useAuthContext } from "../hooks/useContext/useAuthContext";
import { Grid, Tabs, Tab, Button, Box, Typography, Divider } from "@mui/material";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "../hooks/useTranslation";
// import { DndProvider } from 'react-dnd';
// import { HTML5Backend } from 'react-dnd-html5-backend';

const GridContainerMain = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.common.background,
  paddingLeft: "80px",
  paddingRight: "80px",
  paddingTop: "70px",
  width: "100%",
  minHeight: "100vh",
  "@media (max-width: 960px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
    paddingTop: "35px",
  },
}));

const MenuItemsPage = () => {
  const { itemConfigs, dispatch } = useItemConfigContext();
  const { user } = useAuthContext();
  const [tabValue, setTabValue] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [loadingBulkUpdate, setLoadingBulkUpdate] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  
  // Helper function to get translation key from subcategory name
  const getSubcategoryKey = (subcategory) => {
    const keyMap = {
      "Main Plates": "mainPlates",
      "Snacks & Fries": "snacksAndFries",
      "Burgers & Kebab": "burgersAndKebab",
      "Soups": "soups",
      "Pierogi & Regional": "pierogiAndRegional",
      "Desserts": "desserts",
      "Sweets": "sweets",
      "Hot": "hot",
      "Cold": "cold",
      "Alcoholic": "alcoholic",
      "Shots": "shots",
    };
    return keyMap[subcategory] || subcategory;
  };
  
  const subcategoryOptions = {
    Food: [
      "Main Plates",
      "Snacks & Fries",
      "Burgers & Kebab",
      "Soups",
      "Pierogi & Regional",
      "Desserts",
      "Sweets",
    ],
    Grill: [
      "Main Plates",
      "Snacks & Fries",
      "Burgers & Kebab",
      "Soups",
      "Pierogi & Regional",
      "Desserts",
      "Sweets",
    ],
    Drink: ["Hot", "Cold", "Alcoholic", "Shots"],
  };

  const currentSubcategoryKey =
    tabValue === "food"
      ? "Food"
      : tabValue === "grill"
      ? "Grill"
      : tabValue === "drink"
      ? "Drink"
      : null;
  const shouldShowSubcategoryTabs = currentSubcategoryKey && tabValue !== "grill";

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedSubcategory(""); // reset subcategory on main tab change
  };

  const handleSubcategoryChange = (event, newValue) => {
    // "allSub" = show all subcategories for current type
    setSelectedSubcategory(newValue === "allSub" ? "" : newValue);
  };

  // Helper function to check if all items in a category are unavailable
  const areAllItemsUnavailable = (type) => {
    if (!itemConfigs) return false;
    const items = itemConfigs.filter(item => item.itemType === type);
    return items.length > 0 && items.every(item => !item.itemAvailable);
  };

  // Helper function to check if all items in a subcategory are unavailable
  const areAllSubcategoryItemsUnavailable = (subcategory) => {
    if (!itemConfigs) return false;
    const items = itemConfigs.filter(item => item.itemSubcategory === subcategory);
    return items.length > 0 && items.every(item => !item.itemAvailable);
  };

  const handleToggleFoodAvailability = async (setAvailable) => {
    setLoadingBulkUpdate(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URL + "/api/itemconfigs/bulk/food-availability",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ available: setAvailable }),
        }
      );
      if (response.ok) {
        // Refresh the item configs
        const itemsResponse = await fetch(
          process.env.REACT_APP_SERVER_URL + "/api/itemconfigs",
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        const itemsJson = await itemsResponse.json();
        if (itemsResponse.ok) {
          dispatch({ type: "SET_ITEMCONFIG", payload: itemsJson });
        }
      }
    } catch (error) {
      console.error("Error toggling food availability:", error);
    } finally {
      setLoadingBulkUpdate(false);
    }
  };

  const handleToggleDrinkAvailability = async (setAvailable) => {
    setLoadingBulkUpdate(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URL + "/api/itemconfigs/bulk/drink-availability",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ available: setAvailable }),
        }
      );
      if (response.ok) {
        // Refresh the item configs
        const itemsResponse = await fetch(
          process.env.REACT_APP_SERVER_URL + "/api/itemconfigs",
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        const itemsJson = await itemsResponse.json();
        if (itemsResponse.ok) {
          dispatch({ type: "SET_ITEMCONFIG", payload: itemsJson });
        }
      }
    } catch (error) {
      console.error("Error toggling drink availability:", error);
    } finally {
      setLoadingBulkUpdate(false);
    }
  };

  const handleToggleGrillAvailability = async (setAvailable) => {
    setLoadingBulkUpdate(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URL + "/api/itemconfigs/bulk/grill-availability",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ available: setAvailable }),
        }
      );
      if (response.ok) {
        const itemsResponse = await fetch(
          process.env.REACT_APP_SERVER_URL + "/api/itemconfigs",
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        const itemsJson = await itemsResponse.json();
        if (itemsResponse.ok) {
          dispatch({ type: "SET_ITEMCONFIG", payload: itemsJson });
        }
      }
    } catch (error) {
      console.error("Error toggling grill availability:", error);
    } finally {
      setLoadingBulkUpdate(false);
    }
  };

  const handleToggleSubcategoryAvailability = async (subcategory, setAvailable) => {
    setLoadingBulkUpdate(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URL + "/api/itemconfigs/bulk/subcategory-availability",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ subcategory, available: setAvailable }),
        }
      );
      if (response.ok) {
        // Refresh the item configs
        const itemsResponse = await fetch(
          process.env.REACT_APP_SERVER_URL + "/api/itemconfigs",
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        const itemsJson = await itemsResponse.json();
        if (itemsResponse.ok) {
          dispatch({ type: "SET_ITEMCONFIG", payload: itemsJson });
        }
      }
    } catch (error) {
      console.error("Error toggling subcategory availability:", error);
    } finally {
      setLoadingBulkUpdate(false);
    }
  };


  useEffect(() => {
    const fetchItemConfigs = async () => {
      const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/itemconfigs", {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const json = await response.json();
      if (response.ok) {
        dispatch({ type: "SET_ITEMCONFIG", payload: json });
      } else {
        window.location.reload();
      }
    };

    if (user) {
      fetchItemConfigs();
    }
  }, [dispatch, user]);

  const filterItems = () => {
    if (!itemConfigs) return [];

    let filteredItems = [...itemConfigs];

    const availabilityRank = (item) => (item?.itemAvailable === false ? 0 : 1);

    // ALL TAB (keep your existing type order, but also optionally sort within each type by subcategory order)
    if (tabValue === "all") {
      const typeOrder = { Food: 1, Grill: 2, Drink: 3 };

      // build order maps once
      const foodOrder = subcategoryOptions.Food || [];
      const grillOrder = subcategoryOptions.Grill || [];
      const drinkOrder = subcategoryOptions.Drink || [];
      const foodIndex = new Map(foodOrder.map((name, idx) => [name, idx]));
      const grillIndex = new Map(grillOrder.map((name, idx) => [name, idx]));
      const drinkIndex = new Map(drinkOrder.map((name, idx) => [name, idx]));

      return filteredItems
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((a, b) => {
          // 0) Unavailable items first
          const availDiff = availabilityRank(a.item) - availabilityRank(b.item);
          if (availDiff !== 0) return availDiff;

          // 1) Food before Grill before Drink
          const typeDiff =
            (typeOrder[a.item.itemType] ?? 999) - (typeOrder[b.item.itemType] ?? 999);
          if (typeDiff !== 0) return typeDiff;

          // 2) within same type, sort by subcategory order
          const indexMap =
            a.item.itemType === "Food"
              ? foodIndex
              : a.item.itemType === "Grill"
              ? grillIndex
              : drinkIndex;

          const aRank = indexMap.has(a.item.itemSubcategory)
            ? indexMap.get(a.item.itemSubcategory)
            : Number.POSITIVE_INFINITY;

          const bRank = indexMap.has(b.item.itemSubcategory)
            ? indexMap.get(b.item.itemSubcategory)
            : Number.POSITIVE_INFINITY;

          if (aRank !== bRank) return aRank - bRank;

          // 3) stable fallback (keep original order)
          return a.originalIndex - b.originalIndex;
        })
        .map(({ item }) => item);
    }

    // FOOD or DRINK TAB
    const type =
      tabValue === "food" ? "Food" : tabValue === "grill" ? "Grill" : "Drink";
    filteredItems = filteredItems.filter((item) => item.itemType === type);

    // If a specific subcategory is selected, filter only
    if (selectedSubcategory) {
      return filteredItems
        .filter((item) => item.itemSubcategory === selectedSubcategory)
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((a, b) => {
          const availDiff = availabilityRank(a.item) - availabilityRank(b.item);
          if (availDiff !== 0) return availDiff;
          return a.originalIndex - b.originalIndex;
        })
        .map(({ item }) => item);
    }

    // No subcategory selected => sort by subcategory order for this type
    const order = subcategoryOptions[type] || [];
    const orderIndex = new Map(order.map((name, idx) => [name, idx]));

    return filteredItems
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort((a, b) => {
        // 0) Unavailable items first
        const availDiff = availabilityRank(a.item) - availabilityRank(b.item);
        if (availDiff !== 0) return availDiff;

        const aRank = orderIndex.has(a.item.itemSubcategory)
          ? orderIndex.get(a.item.itemSubcategory)
          : Number.POSITIVE_INFINITY;

        const bRank = orderIndex.has(b.item.itemSubcategory)
          ? orderIndex.get(b.item.itemSubcategory)
          : Number.POSITIVE_INFINITY;

        if (aRank !== bRank) return aRank - bRank;

        // stable fallback
        return a.originalIndex - b.originalIndex;
      })
      .map(({ item }) => item);
  };

  return (
    //allows drag and drop on screen
    // <DndProvider backend={HTML5Backend}>
    <div style={{backgroundColor: theme.palette.common.background}}>
      <DashboardNavBar />
      <GridContainerMain container spacing={{xs:3, sm:3, md:3, lg:4, xl:6}}>
        <Grid item xs={12} sm={12} md={12} lg={7} xl={8} order={{ xs: 2, lg: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            centered
            sx={{ width: "100%", marginBottom: "1rem" }}
          >
            <Tab label={t('all')} value="all" />
            <Tab 
              label={t('food')} 
              value="food"
              sx={{
                '&.Mui-selected': {
                  color: areAllItemsUnavailable('Food') ? theme.palette.error.main : theme.palette.primary.main
                }
              }}
            />
            <Tab 
              label={t('grill')} 
              value="grill"
              sx={{
                '&.Mui-selected': {
                  color: areAllItemsUnavailable('Grill') ? theme.palette.error.main : theme.palette.primary.main
                }
              }}
            />
            <Tab 
              label={t('drinks')} 
              value="drink"
              sx={{
                '&.Mui-selected': {
                  color: areAllItemsUnavailable('Drink') ? theme.palette.error.main : theme.palette.primary.main
                }
              }}
            />
          </Tabs>

          {/* SUBCATEGORY TABS – only when Food or Drinks is selected */}
          {shouldShowSubcategoryTabs && (
            <Tabs
              value={selectedSubcategory || "allSub"}
              onChange={handleSubcategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                width: "100%",
                marginBottom: "1rem",
                "& .MuiTabs-flexContainer": {
                  justifyContent: "center",
                },
              }}
            >
              <Tab
                label={
                  currentSubcategoryKey === "Food"
                    ? t('allFood')
                    : currentSubcategoryKey === "Grill"
                    ? t('allGrill')
                    : t('allDrinks')
                }
                value="allSub"
                sx={{
                  '&.Mui-selected': {
                    color: areAllItemsUnavailable(currentSubcategoryKey) ? theme.palette.error.main : theme.palette.primary.main
                  }
                }}
              />
              {subcategoryOptions[currentSubcategoryKey].map((sub) => (
                <Tab 
                  key={sub} 
                  label={t(getSubcategoryKey(sub))} 
                  value={sub}
                  sx={{
                    '&.Mui-selected': {
                      color: areAllSubcategoryItemsUnavailable(sub) ? theme.palette.error.main : theme.palette.primary.main
                    }
                  }}
                />
              ))}
            </Tabs>
          )}
          {/* TOGGLE FOOD AVAILABILITY BUTTONS – only when Food tab is selected and 'All Food' subtab */}
          {tabValue === "food" && !selectedSubcategory && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", marginBottom: "1rem" }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleToggleFoodAvailability(true)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('loading') : t('markAllAvailable')}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleToggleFoodAvailability(false)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('loading') : t('markAllUnavailable')}
              </Button>
            </Box>
          )}
          {tabValue === "grill" && !selectedSubcategory && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", marginBottom: "1rem" }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleToggleGrillAvailability(true)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('loading') : t('markAllAvailable')}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleToggleGrillAvailability(false)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('loading') : t('markAllUnavailable')}
              </Button>
            </Box>
          )}
          {/* TOGGLE DRINK AVAILABILITY BUTTONS – only when Drink tab is selected and 'All Drinks' subtab */}
          {tabValue === "drink" && !selectedSubcategory && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", marginBottom: "1rem" }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleToggleDrinkAvailability(true)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('loading') : t('markAllAvailable')}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleToggleDrinkAvailability(false)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('loading') : t('markAllUnavailable')}
              </Button>
            </Box>
          )}
          {/* TOGGLE SUBCATEGORY AVAILABILITY BUTTONS – only when a specific subcategory is selected */}
          {selectedSubcategory && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", marginBottom: "1rem" }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleToggleSubcategoryAvailability(selectedSubcategory, true)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('updating') : `${t('enableAll')} ${t(getSubcategoryKey(selectedSubcategory))}`}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleToggleSubcategoryAvailability(selectedSubcategory, false)}
                disabled={loadingBulkUpdate}
              >
                {loadingBulkUpdate ? t('updating') : `${t('disableAll')} ${t(getSubcategoryKey(selectedSubcategory))}`}
              </Button>
            </Box>
          )}
          <Grid container spacing={3}>
            {(() => {
              const items = filterItems();
              const unavailableItems = items.filter((item) => item.itemAvailable === false);
              const availableItems = items.filter((item) => item.itemAvailable !== false);

              const renderItems = (list) =>
                list.map((itemConfig) => (
                  <Grid item xs={12} sm={6} md={6} lg={6} xl={4} key={itemConfig._id}>
                    <ItemConfigDetails key={itemConfig._id} itemConfig={itemConfig} />
                  </Grid>
                ));

              return (
                <>
                  {unavailableItems.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.error.main }}>
                          {t('unavailable')}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    </Grid>
                  )}
                  {renderItems(unavailableItems)}

                  {availableItems.length > 0 && unavailableItems.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                          {t('available')}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    </Grid>
                  )}
                  {renderItems(availableItems)}
                </>
              );
            })()}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={5} xl={4} order={{ xs: 1, lg: 2 }}>
          <ItemConfigForm />
        </Grid>
      </GridContainerMain>
      <Footer />
    </div>
    // </DndProvider>
  );
};

export default MenuItemsPage;